/**
 * @file App.jsx
 * @description Root application component — manages routing, theming, and auth context.
 *              All protected routes are wrapped in ProtectedRoute + MainLayout.
 */
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import { DeviceProvider } from './store/DeviceStore';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Layouts & Pages
import MainLayout from './components/layout/MainLayout';
import LoginPage from './components/pages/LoginPage';
import AnalysisPage from './components/pages/AnalysisPage'; // Fleet economics: utilization, idle, latency, PM compliance, conversion, revenue
import ControlPanelPage from './components/pages/ControlPanelPage'; // Live ops command centre for rental + service teams
import HardwarePage from './components/pages/HardwarePage'; // Telematics hardware monitor (first ign/engine/CAN per device)
import MEWPMonitoringPage from './components/pages/MEWPMonitoringPage';
import MEWPDetailPage from './components/pages/MEWPDetailPage';
import MEWPDataPage from './components/pages/MEWPDataPage';
import ServicePage from './components/pages/ServicePage';
import ServiceTicketDetail from './components/pages/ServiceTicketDetail';
import ServiceAnalytics from './components/pages/ServiceAnalytics';
import NewServiceTicket from './components/pages/NewServiceTicket';
import ServiceMonitorPage from './components/pages/ServiceMonitorPage';
import RentalPage from './components/pages/RentalPage';
import RentalDetailsPage from './components/pages/RentalDetailsPage';
import RentalMachineDetailPage from './components/pages/RentalMachineDetailPage';
import ServiceMachineDetailPage from './components/pages/ServiceMachineDetailPage';
import ContractDetailsPage from './components/pages/ContractDetailsPage';
import ContractLiveViewPage from './components/pages/ContractLiveViewPage';
import ContractDataPage from './components/pages/ContractDataPage';
import ContractSummaryPage from './components/pages/ContractSummaryPage';
import LogPage from './components/pages/LogPage';
import GeofencingPage from './components/pages/GeofencingPage';
import BillingPage from './components/pages/BillingPage';
import SettingsPage from './components/pages/SettingsPage';
import AlarmsPage from './components/pages/AlarmsPage';
import MaintenancePage from './components/pages/MaintenancePage';
import TowerLightPage from './components/pages/TowerLightPage';
import BatteryPackPage from './components/pages/BatteryPackPage';
import EquipmentPage from './components/pages/EquipmentPage';
// Department-owned pages — every department mounts its OWN file per header
// (shared engines underneath), so no department's screen is another's.
import ServiceControlPanelPage from './components/pages/service/ServiceControlPanelPage';
import ServiceEquipmentPage from './components/pages/service/ServiceEquipmentPage';
import ServiceDailyReportPage from './components/pages/service/ServiceDailyReportPage';
import ServiceMaintenancePage from './components/pages/service/ServiceMaintenancePage';
import ServiceJobCardsPage from './components/pages/service/ServiceJobCardsPage';
import ServiceErrorsPage from './components/pages/service/ServiceErrorsPage';
import ServiceDiagnosisPage from './components/pages/service/ServiceDiagnosisPage';
import ServiceHardwarePage from './components/pages/service/ServiceHardwarePage';
import ServicePartsCatalogPage from './components/pages/service/ServicePartsCatalogPage';
import PartsCatalogPage from './components/pages/PartsCatalogPage';
import SalesControlPanelPage from './components/pages/sales/SalesControlPanelPage';
import SalesContractsPage from './components/pages/sales/SalesContractsPage';
import SalesCustomersPage from './components/pages/sales/SalesCustomersPage';
import SalesEquipmentPage from './components/pages/sales/SalesEquipmentPage';
import OperationsControlPanelPage from './components/pages/operations/OperationsControlPanelPage';
import OperationsDailyReportPage from './components/pages/operations/OperationsDailyReportPage';
import OperationsEquipmentPage from './components/pages/operations/OperationsEquipmentPage';
import OperationsSitesPage from './components/pages/operations/OperationsSitesPage';
import OperationsSignalCoveragePage from './components/pages/operations/OperationsSignalCoveragePage';
import OperationsMachineContractsPage from './components/pages/operations/OperationsMachineContractsPage';
import OperationsErrorsPage from './components/pages/operations/OperationsErrorsPage';
import OperationsIssuesPage from './components/pages/operations/OperationsIssuesPage';
import OperationsLogSheetsPage from './components/pages/operations/OperationsLogSheetsPage';
import FinancialAnalysisPage from './components/pages/financial/FinancialAnalysisPage';
import FinancialBillingPage from './components/pages/financial/FinancialBillingPage';
import DataManagementPage from './components/pages/DataManagementPage';
import SparePartsPage from './components/pages/SparePartsPage';
import InventoryControlPanelPage from './components/pages/inventory/InventoryControlPanelPage';
import OrganizationPage from './components/pages/OrganizationPage';
import ProcurementPage from './components/pages/ProcurementPage';
import MailPage from './components/pages/MailPage';
import EmailIntakePage from './components/pages/EmailIntakePage';
import ChatPage from './components/pages/ChatPage';
import TaskPage from './components/pages/TaskPage';
import ProfilePage from './components/pages/ProfilePage';
import CalendarPage from './components/pages/CalendarPage';
import HowToUsePage from './components/pages/HowToUsePage';       // per-department user guide (everyone)
import CreatorNewsPage from './components/pages/CreatorNewsPage'; // creator's updates (everyone reads)
import SinoboomSettingsPage from './components/pages/SinoboomSettingsPage'; // Sinoboom i-Link integration (super admin)
import DeviceConfigPage from './components/pages/DeviceConfigPage';
import DeviceConfigControlPanelPage from './components/pages/deviceConfig/DeviceConfigControlPanelPage';
import DebugConsolePage from './components/pages/DebugConsolePage';
import SmsPage from './components/pages/SmsPage';
import DailyReportPage from './components/pages/DailyReportPage'; // Ops daily report (ran / not-run / moved / overtime)
import NotFound from './components/pages/NotFound';
import { useAuth } from './context/AuthContext';
import { useLocation } from 'react-router-dom';
import { canViewPath, landingPath } from './utils/permissions';

/**
 * @description Page-level access guard. Every gated route is checked against the
 * user's permissions (see utils/permissions.js). Users who reach a page they
 * are not granted are redirected to their landing page (Control Panel first,
 * else the first page they can open). The super admin bypasses all gates.
 */
const PermissionGuard = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();

  if (!canViewPath(user, location.pathname)) {
    return <Navigate to={landingPath(user)} replace />;
  }

  return children;
};

/** Index redirect — opening the app lands on Control Panel (or first allowed page). */
const IndexRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={landingPath(user)} replace />;
};

function App() {
  return (
    <AuthProvider>
      <DeviceProvider>
        <Helmet>
          <title>MTANDT | IoT Dashboard</title>
          <meta name="theme-color" content="#ffffff" />
        </Helmet>

        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />

              {/* Private Routes (Wrapped in ProtectedRoute and MainLayout) */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <PermissionGuard>
                        <Routes>
                        {/* App opens on Control Panel (or the user's first allowed page) */}
                        <Route path="/" element={<IndexRedirect />} />
                        {/* Analysis: the fleet-economics dashboards (replaced Overview + Dashboard) */}
                        <Route path="/analysis" element={<AnalysisPage />} />
                        {/* Control panel: live fleet ops screen for rental + service teams */}
                        <Route path="/control-panel" element={<ControlPanelPage />} />
                        {/* Hardware: telematics device monitor (first ign/engine/CAN per device) */}
                        <Route path="/hardware" element={<HardwarePage />} />

                        {/* Department-owned pages: one route + one file per header.
                            Access is department-gated (utils/permissions.js) —
                            only that department's users and the super admin. */}
                        <Route path="/service/control-panel" element={<ServiceControlPanelPage />} />
                        <Route path="/service/equipment" element={<ServiceEquipmentPage />} />
                        <Route path="/service/daily-report" element={<ServiceDailyReportPage />} />
                        <Route path="/service/maintenance" element={<ServiceMaintenancePage />} />
                        <Route path="/service/job-cards" element={<ServiceJobCardsPage />} />
                        <Route path="/service/errors" element={<ServiceErrorsPage />} />
                        <Route path="/service/diagnosis" element={<ServiceDiagnosisPage />} />
                        <Route path="/service/hardware" element={<ServiceHardwarePage />} />
                        <Route path="/service/parts-catalog" element={<ServicePartsCatalogPage />} />
                        <Route path="/sales/control-panel" element={<SalesControlPanelPage />} />
                        <Route path="/sales/contracts" element={<SalesContractsPage />} />
                        <Route path="/sales/customers" element={<SalesCustomersPage />} />
                        <Route path="/sales/equipment" element={<SalesEquipmentPage />} />
                        <Route path="/operations/control-panel" element={<OperationsControlPanelPage />} />
                        <Route path="/operations/daily-report" element={<OperationsDailyReportPage />} />
                        <Route path="/operations/equipment" element={<OperationsEquipmentPage />} />
                        <Route path="/operations/sites" element={<OperationsSitesPage />} />
                        <Route path="/operations/signal-coverage" element={<OperationsSignalCoveragePage />} />
                        <Route path="/operations/machine-contracts" element={<OperationsMachineContractsPage />} />
                        <Route path="/operations/errors" element={<OperationsErrorsPage />} />
                        <Route path="/operations/issues" element={<OperationsIssuesPage />} />
                        <Route path="/operations/log-sheets" element={<OperationsLogSheetsPage />} />
                        <Route path="/financial/analysis" element={<FinancialAnalysisPage />} />
                        <Route path="/financial/billing" element={<FinancialBillingPage />} />

                        {/* Main Navigation */}
                        <Route path="/geofencing" element={<GeofencingPage />} />
                        {/* The equipment inventory ("devices" = TCUs in this product,
                            so the machine list is named what it is). /devices kept
                            as a redirect for old links; /devices/:id still resolves. */}
                        <Route path="/equipment" element={<EquipmentPage />} />
                        <Route path="/devices" element={<Navigate to="/equipment" replace />} />
                        <Route path="/data-management" element={<DataManagementPage />} />
                        <Route path="/billing" element={<BillingPage />} />
                        <Route path="/rentals" element={<RentalPage />} />
                        <Route path="/rental-details/:rentalId" element={<RentalDetailsPage />} />
                        <Route path="/contract/:contractId" element={<ContractDetailsPage />} />
                        <Route path="/contract-live/:contractId" element={<ContractLiveViewPage />} />
                        <Route path="/contract-data/:contractId" element={<ContractDataPage />} />
                        <Route path="/contract-summary/:contractId" element={<ContractSummaryPage />} />
                        <Route path="/rental-log/:rentalId" element={<LogPage />} />
                        <Route path="/inventory/control-panel" element={<InventoryControlPanelPage />} />
                        <Route path="/inventory" element={<SparePartsPage />} />
                        <Route path="/parts-catalog" element={<PartsCatalogPage />} />
                        <Route path="/organization" element={<OrganizationPage />} />
                        <Route path="/procurement" element={<ProcurementPage />} />

                        {/* Device Categories */}
                        <Route path="/mewp" element={<MEWPMonitoringPage />} />
                        <Route path="/mewp-detail/:deviceId" element={<MEWPDetailPage />} />
                        <Route path="/mewp-data/:deviceId" element={<MEWPDataPage />} />
                        <Route path="/battery-packs" element={<BatteryPackPage />} />
                        <Route path="/tower-lights" element={<TowerLightPage />} />
                        <Route path="/tower-lights/:deviceId" element={<TowerLightPage />} />
                        <Route path="/devices/:deviceId" element={<MEWPDetailPage />} />
                        <Route path="/rental-machine/:equipmentId" element={<RentalMachineDetailPage />} />
                        <Route path="/service-machine/:equipmentId" element={<ServiceMachineDetailPage />} />
                        <Route path="/service" element={<ServicePage />} />
                        <Route path="/service/monitor" element={<ServiceMonitorPage />} />
                        <Route path="/service/new-ticket" element={<NewServiceTicket />} />
                        <Route path="/service/tickets/:ticketId" element={<ServiceTicketDetail />} />
                        <Route path="/service/analytics" element={<ServiceAnalytics />} />

                        {/* Other routes */}
                        <Route path="/alarms" element={<AlarmsPage />} />
                        <Route path="/maintenance" element={<MaintenancePage />} />
                        <Route path="/mail" element={<MailPage />} />
                        <Route path="/email-intake" element={<EmailIntakePage />} />
                        <Route path="/chat" element={<ChatPage />} />
                        <Route path="/tasks" element={<TaskPage />} />
                        <Route path="/device-config/control-panel" element={<DeviceConfigControlPanelPage />} />
                        <Route path="/device-config" element={<DeviceConfigPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="/calendar" element={<CalendarPage />} />
                        <Route path="/how-to-use" element={<HowToUsePage />} />
                        <Route path="/creator-news" element={<CreatorNewsPage />} />
                        <Route path="/sinoboom-settings" element={<SinoboomSettingsPage />} />
                        <Route path="/debug" element={<DebugConsolePage />} />
                        <Route path="/sms" element={<SmsPage />} />
                        <Route path="/reports/daily" element={<DailyReportPage />} />

                        <Route path="*" element={<NotFound />} />
                        </Routes>
                      </PermissionGuard>
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Router>
        </ThemeProvider>
      </DeviceProvider>
    </AuthProvider>
  );
}

export default App;